import React from 'react'
import { Link } from 'react-router-dom'
import appConstants from './../../../../constants/appConstants'
import Telephone from '../../../../components/shared/Telephone/Telephone.component'
import ComplianceDisclaimer from '../../../../components/shared/ComplianceDisclaimer/ComplianceDisclaimer.component'
import { phoneToStorage } from './../../../../shared/formatters/telephone.formatter'
import { getPhone, getMobilePhone, getEmail } from './../../formatters'
import isFlagOn from './../../../../shared/utilities/isFlagOn.function'
import { getAgentLogo } from './../../branding'
import CoBrandLogo from './../CoBrandLogo/CoBrandLogo.component'

import './AgentFooter.component.scss'

const SOCIALS = [
  {
    key: 's_facebook_link',
    icon: 'facebook',
    base: 'https://www.facebook.com/',
  },
  {
    key: 's_instagram_link',
    icon: 'instagram',
    base: 'https://www.instagram.com/',
  },
  {
    key: 's_linkedin_link',
    icon: 'linkedin',
    base: 'https://www.linkedin.com/in/',
  },
  { key: 's_twitter_link', icon: 'twitter', base: 'https://twitter.com/' },
]

const AgentFooter = ({ subsiteData, goTo, isResolved }) => {
  const login = subsiteData?.u_login
  const onNav = (event, link) => {
    event.preventDefault()
    if (goTo) goTo(link)
    return false
  }

  const phone = getPhone(subsiteData)
  const mobile = getMobilePhone(subsiteData)
  const email = getEmail(subsiteData)
  const showAddress = isFlagOn(subsiteData?.s_fulladdress) && (subsiteData?.u_address1 || subsiteData?.u_city)
  const addressLine2 = [subsiteData?.u_city ? `${subsiteData.u_city},` : '', subsiteData?.u_state, subsiteData?.u_zip]
    .filter(n => n)
    .join(' ')

  const socials = SOCIALS.filter(s => subsiteData?.[s.key])
  // Licensed-producer name must be the legal name, so this deliberately does
  // NOT use getFullName() — that helper prefers u_nickname.
  const legalName = [subsiteData?.u_fname, subsiteData?.u_lname]
    .filter(n => n)
    .join(' ')
    .trim()

  const agentLogo = getAgentLogo(subsiteData)
  const logoResolved = isResolved !== false
  const agentLogoAlt = legalName || 'Agency logo'

  return (
    <footer id='AgentFooterComponent'>
      <div className='footer-main'>
        {/* Brand + disclaimer */}
        <div className='footer-col footer-brand'>
          <Link to={login ? `/${login}` : '/'}>
            {!logoResolved ? (
              <span className='brand-logo-skeleton' />
            ) : agentLogo ? (
              <CoBrandLogo
                agentLogo={agentLogo}
                agentLogoAlt={agentLogoAlt}
                partnerLogo={appConstants.logos.plain.md}
                layout='stacked'
              />
            ) : (
              <img alt='USA Benefits Group' src={appConstants.logos.plain.md} />
            )}
          </Link>
          <p className='footer-disclaimer'>
            A non-government website powered by USA Benefits Group, a health insurance agency.
          </p>
        </div>

        {/* Agent contact (center) */}
        <div className='footer-col footer-contact-col'>
          <ul className='footer-contact'>
            {phone ? (
              <li>
                <span className='footer-label'>Phone:</span>
                <Telephone phoneNumber={getPhone(subsiteData, phoneToStorage)} className='link-white' />
              </li>
            ) : (
              <></>
            )}
            {mobile ? (
              <li>
                <span className='footer-label'>Mobile:</span>
                <Telephone phoneNumber={getMobilePhone(subsiteData, phoneToStorage)} className='link-white' />
              </li>
            ) : (
              <></>
            )}
            {email ? (
              <li>
                <span className='footer-label'>Email:</span>
                <a href={`mailto:${email}`} className='link-white'>
                  {email}
                </a>
              </li>
            ) : (
              <></>
            )}
            {showAddress ? (
              <li>
                <span className='footer-label'>Office:</span>
                <span>
                  {subsiteData?.u_address1 ? `${subsiteData.u_address1}, ` : ''}
                  {addressLine2}
                </span>
              </li>
            ) : (
              <></>
            )}
          </ul>
          {socials.length ? (
            <ul className='footer-socials'>
              {socials.map(s => (
                <li key={s.key}>
                  <a
                    href={`${s.base}${subsiteData[s.key]}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    aria-label={s.icon}
                    className={`fa fa-${s.icon}`}
                  >
                    {''}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <></>
          )}
          <div className='footer-copyright'>
            &copy;&nbsp;{new Date().getFullYear()} All Rights Reserved | USA Benefits Group
          </div>
        </div>

        {/* Quick links */}
        <div className='footer-col footer-links-col'>
          <ul className='footer-links'>
            <li>
              <a href={login ? `/${login}` : '/'} onClick={e => onNav(e, `/${login}`)}>
                Home
              </a>
            </li>
            <li>
              <a href={login ? `/${login}/products` : '#'} onClick={e => onNav(e, `/${login}/products`)}>
                Products
              </a>
            </li>
            <li>
              <a href='/careers' onClick={e => onNav(e, '/careers')}>
                Careers
              </a>
            </li>
            <li>
              <a href={login ? `/${login}/contact` : '#'} onClick={e => onNav(e, `/${login}/contact`)}>
                Get a Quote
              </a>
            </li>
          </ul>
        </div>

        {/* Compliance — full-width row beneath the three columns (UAD-3739) */}
        <ComplianceDisclaimer variant='long' agentName={legalName} tone='light' className='footer-compliance' />
      </div>

      {/* Legal — full-width strip */}
      <div className='footer-legal'>
        <p className='footer-medicare'>
          We do not offer every plan available in your area. We currently represent 88 organizations. Any information we
          provide is limited to those plans we do offer in your area. Please contact{' '}
          <a href='http://medicare.gov/'>medicare.gov</a>, 1-800-MEDICARE, or your local State Health Insurance Program
          (SHIP) to get information on all of your options. Not connected with or endorsed by the United States
          Government or the Federal Medicare Program. This is solicitation for insurance.
        </p>
      </div>
    </footer>
  )
}

export default AgentFooter
